import { serviceAreas } from '@markku/db'
import { createInsertSchema, createSelectSchema } from 'drizzle-zod'
import { z } from 'zod'

// A validation bound, not a data-model one: raise it when a longer rotation
// is wanted.
export const MAX_ROTATION_WEEKS = 8

export const selectServiceAreaSchema = createSelectSchema(serviceAreas)

// A comma or a swapped pair would otherwise reach Postgres as a 500, or
// put the area in the sea.
const coordinate = (limit: number) =>
  z.string().refine((value) => {
    const number = Number(value)
    return (
      value.trim() !== '' &&
      Number.isFinite(number) &&
      Math.abs(number) <= limit
    )
  }, 'Virheellinen koordinaatti')

export const insertServiceAreaSchema = createInsertSchema(serviceAreas, {
  latitude: coordinate(90),
  longitude: coordinate(180),
}).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  deletedAt: true,
})

export const updateServiceAreaSchema = insertServiceAreaSchema.partial()

const rotationDaySchema = z.object({
  weekIndex: z.number().int().min(0),
  weekday: z.number().int().min(1).max(7),
  serviceAreaId: z.number(),
})

export const employeeRotationSchema = z.object({
  weeks: z.number().int().min(1),
  days: z.array(rotationDaySchema),
})

// The cap bounds what may be saved, not what is read back.
export const setEmployeeRotationSchema = employeeRotationSchema
  .extend({
    employeeId: z.number(),
    weeks: z.number().int().min(1).max(MAX_ROTATION_WEEKS),
  })
  .refine(
    ({ weeks, days }) => days.every((day) => day.weekIndex < weeks),
    'Kierron päivä on kierron pituuden ulkopuolella',
  )
  .refine(
    ({ days }) =>
      new Set(days.map((day) => `${day.weekIndex}-${day.weekday}`)).size ===
      days.length,
    'Samalle päivälle on annettu kaksi aluetta',
  )

export type ServiceArea = z.infer<typeof selectServiceAreaSchema>
export type CreateServiceArea = z.infer<typeof insertServiceAreaSchema>
export type UpdateServiceArea = z.infer<typeof updateServiceAreaSchema>
export type EmployeeRotation = z.infer<typeof employeeRotationSchema>
export type EmployeeRotationDay = z.infer<typeof rotationDaySchema>

export const rotationCoverageSchema = z.array(
  z.object({
    serviceAreaId: z.number(),
    employeeName: z.string(),
    weekIndex: z.number(),
    weekday: z.number(),
    rotationWeeks: z.number(),
  }),
)

export type RotationCoverage = z.infer<typeof rotationCoverageSchema>
